import React from 'react';
import PortableText from 'libs/sanity/components/PortableText';
import type { ButtonLinkArrayComponent, RichTextBlock } from 'libs/sanity/types/generated';
import styled, { type CSSProps } from 'libs/styled';
import Button from 'components/Button';
import { above } from '@fogia/core-web/utils/mediaqueries';
import { richTextTagStyles } from 'libs/sanity/components/PortableText/richTextTagStyles';

export interface RichTextComponentProps {
    richText?: RichTextBlock['richText'];
    buttons?: RichTextBlock['buttons'];
    /** Layout from ColumnModule */
    layout?: 'fullWidth' | 'halfWidth' | 'quarterWidth';
}

const Wrapper = styled('div')<CSSProps>`
    ${richTextTagStyles}

    &:not([data-layout='halfWidth']):not([data-layout='quarterWidth']) {
        ${above['tablet.lg']} {
            max-width: 50%;
            margin: 0 auto 0 0;
        }
    }

    /* layout=halfWidth overrides */
    &[data-layout='halfWidth'] {
        h2 {
            font-size: 18px;

            ${above['tablet.lg']} {
                font-size: 20px;
                line-height: 120%;
            }
        }
    }

    /* layout=quarterWidth overrides */
    &[data-layout='quarterWidth'] {
        h1 {
            font-size: 18px;
            line-height: 120%;
            letter-spacing: -0.03em;
            font-weight: 510;

            ${above['tablet.lg']} {
                font-size: 20px;
                line-height: 120%;
                letter-spacing: -0.03em;
                font-weight: 510;
            }
        }

        h2 {
            font-size: 13px;
            line-height: 120%;
            letter-spacing: -0.01em;
            font-weight: 510;

            ${above['tablet.lg']} {
                font-size: 15px;
                line-height: 120%;
                letter-spacing: -0.01em;
                font-weight: 510;
            }
        }
    }
`;

const ButtonsWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    gap: 10px;
`;

const getTypography = (layout?: RichTextComponentProps['layout']): string | Array<string | null> => {
    if (layout === 'quarterWidth') {
        return ['Primary/Mobile/13_100_510_-1__Indent', null, null, null, 'Primary/15_120_510_-1_Indent'];
    }

    return ['Secondary/Mobile/18_120_400_-1_Indent', null, null, null, 'Secondary/20_120_400_-1_Indent'];
};

const RichTextBlockComponent = ({ richText, buttons, layout }: RichTextComponentProps) => {
    const hasRichText = Array.isArray(richText) && richText.length > 0;
    const hasButtons = Array.isArray(buttons) && buttons.length > 0;

    if (!hasRichText && !hasButtons) {
        return null;
    }

    const buttonSize = layout === 'quarterWidth' ? ('small' as const) : ('large' as const);

    return (
        <Wrapper className="RichText" data-layout={layout}>
            {hasRichText && <PortableText value={richText} typography={getTypography(layout)} />}
            {hasButtons && (
                <ButtonsWrapper>
                    {buttons.map((button: ButtonLinkArrayComponent[number], index) => {
                        if (button.type !== 'buttonLinkComponent') {
                            return null;
                        }
                        return (
                            <Button
                                key={button.key ?? `button-${index}`}
                                theme="textButtonUnderline"
                                sanityLink={button}
                                size={buttonSize}
                            >
                                {button.label}
                            </Button>
                        );
                    })}
                </ButtonsWrapper>
            )}
        </Wrapper>
    );
};

export default RichTextBlockComponent;
